import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Dashboard.module.css";
import {
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { fetchDashboardSummary } from "../../api";
import Empty from "../svgIcons/Empty";
import { Order } from "./Icons";
import Spinner from "../Spinner/Spinner";

const Dashboard = () => {
  const history = useHistory();
  const user = useMemo(() => JSON.parse(localStorage.getItem("profile")), []);
  const [dueDays, setDueDays] = useState(5);
  const [summary, setSummary] = useState({
    days: 5,
    todayBookingsCount: 0,
    inactiveClients: [],
    inactiveClientsCount: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async (days, showRefresh = false) => {
    try {
      setError("");
      showRefresh ? setRefreshing(true) : setLoading(true);
      const { data } = await fetchDashboardSummary(days);
      setSummary(data);
    } catch (err) {
      console.error("Dashboard summary failed", err);
      setError("Unable to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    loadDashboard(5);
  }, [history, loadDashboard, user]);

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return summary.inactiveClients || [];

    return (summary.inactiveClients || []).filter((client) =>
      `${client.name || ""} ${client.agentCode || ""}`
        .toLowerCase()
        .includes(query)
    );
  }, [searchQuery, summary.inactiveClients]);

  const handleDueDaysChange = (event) => {
    const nextValue = Math.max(0, Number(event.target.value || 0));
    setDueDays(nextValue);
  };

  const handleSubmit = () => {
    loadDashboard(dueDays, true);
  };

  const renderLastInvoice = (date) => {
    if (!date) return "Never";
    return moment(date).format("MMM DD, YYYY");
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div>
          <Typography className={styles.eyebrow}>Dashboard</Typography>
          <h1 className={styles.title}>Booking Overview</h1>
        </div>
        <div className={styles.datePill}>{moment().format("Do MMM YYYY")}</div>
      </div>

      <section className={styles.dashboardGrid}>
        <Link
          to={{ pathname: "/todayBookings", state: { fromDashboard: true } }}
          className={styles.bookingCard}
        >
          <div className={styles.bookingContent}>
            <span className={styles.cardLabel}>Today's Bookings</span>
            <strong>{summary.todayBookingsCount}</strong>
            <span className={styles.cardMeta}>
              View bookings for {moment().format("Do MMM YYYY")}
            </span>
          </div>
          <span className={styles.cardIcon}>
            <Order />
          </span>
        </Link>

        <section className={styles.inactivePanel}>
          <div className={styles.panelHeader}>
            <div>
              <Typography className={styles.panelEyebrow}>Agent Activity</Typography>
              <h2>Zero Booking Follow-up</h2>
            </div>
            <div className={styles.filterControl}>
              <span>Past</span>
              <input
                id="dueDays"
                type="number"
                min="0"
                value={dueDays}
                onChange={handleDueDaysChange}
              />
              <span>days</span>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={18} /> : "Go"}
              </Button>
            </div>
          </div>

          <div className={styles.infoBanner}>
            <strong>
              Found {summary.inactiveClientsCount} inactive agents with 0 bookings
              in the last {summary.days} days
            </strong>
            <span>
              Includes agents with no recent activity and new agents with no bookings.
            </span>
          </div>

          <TextField
            className={styles.searchInput}
            fullWidth
            variant="outlined"
            placeholder="Search by agent name..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />

          <div className={styles.tableShell}>
            {error ? (
              <div className={styles.emptyState}>{error}</div>
            ) : filteredClients.length === 0 ? (
              <div className={styles.emptyState}>
                <Empty />
                <p>No inactive agents found for this search.</p>
              </div>
            ) : (
              <table className={styles.clientTable}>
                <thead>
                  <tr>
                    <th>Agent Name</th>
                    <th>Last Invoice</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((agent) => (
                    <tr key={agent._id}>
                      <td data-label="Agent Name">{agent.name}</td>
                      <td data-label="Last Invoice">
                        {renderLastInvoice(agent.lastInvoiceDate)}
                      </td>
                      <td data-label="Status">
                        <span
                          className={
                            agent.status === "New Client"
                              ? styles.newClientBadge
                              : styles.inactiveBadge
                          }
                        >
                          {agent.status === "New Client" ? "New Agent" : agent.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default Dashboard;
